import { useContext, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Modal from '../ui/Modal.jsx'
import { apiFetch } from '../../lib/api.js'
import { HostShellContext } from '../../pages/HostDecksPage.jsx'

// Formulaire sous-thème
function SousThemeForm({ initial, onSubmit, onCancel, loading }) {
  const [form, setForm] = useState(initial ?? { nom: '', ordre: 0 })
  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(form) }} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Nom du sous-thème</label>
        <input
          required
          value={form.nom}
          onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="ex. Les capitales d'Europe"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Ordre d'affichage</label>
        <input
          type="number" min={0}
          value={form.ordre}
          onChange={e => setForm(f => ({ ...f, ordre: parseInt(e.target.value) }))}
          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{ colorScheme: 'dark' }}
        />
      </div>
      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-white/60 hover:text-white transition">
          Annuler
        </button>
        <button type="submit" disabled={loading} className="btn-primary px-6 py-2 disabled:opacity-60">
          {loading ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </form>
  )
}

export default function DeckDetail({ basePath = '/admin/decks', darkMode = false }) {
  const { deckId } = useParams()
  const navigate = useNavigate()
  const shellCtx = useContext(HostShellContext)
  const [deck, setDeck] = useState(null)
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // null | 'create' | sous-thème objet
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function fetchDeck() {
    setLoading(true)
    try {
      const data = await apiFetch(`/api/decks/${deckId}`)
      data.sous_themes?.sort((a, b) => a.ordre - b.ordre)
      setDeck(data)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  useEffect(() => { fetchDeck() }, [deckId])

  useEffect(() => {
    if (!shellCtx) return
    if (deck) shellCtx.setDeckMeta({ title: deck.titre, deckId })
    shellCtx.setBgImage?.(deck?.themes?.image_url ?? null)
    return () => {
      shellCtx.setDeckMeta(null)
      shellCtx.setBgImage?.(null)
    }
  }, [deck?.titre, deck?.themes?.image_url, deckId])

  async function handleSubmit(form) {
    setSaving(true)
    setError('')
    try {
      if (modal === 'create') {
        await apiFetch(`/api/decks/${deckId}/sous-themes`, {
          method: 'POST',
          body: JSON.stringify(form),
        })
      } else {
        await apiFetch(`/api/decks/${deckId}/sous-themes/${modal.id}`, {
          method: 'PUT',
          body: JSON.stringify(form),
        })
      }
      setModal(null)
      await fetchDeck()
    } catch (e) {
      setError(e.message)
    }
    setSaving(false)
  }

  async function handleDelete(st) {
    if (!confirm(`Supprimer le sous-thème "${st.nom}" ?`)) return
    setError('')
    try {
      await apiFetch(`/api/decks/${deckId}/sous-themes/${st.id}`, { method: 'DELETE' })
      setDeck(d => ({ ...d, sous_themes: d.sous_themes.filter(s => s.id !== st.id) }))
    } catch (e) {
      setError(e.message)
    }
  }

  if (loading) return <div className={`py-12 text-center ${darkMode ? 'text-white/40' : 'text-white/40'}`}>Chargement…</div>
  if (!deck) return <div className={`py-12 text-center ${darkMode ? 'text-red-300' : 'text-red-300'}`}>{error || 'Quiz introuvable'}</div>

  return (
    <div>
      {/* Fil d'Ariane — admin uniquement (en mode host c'est dans le header) */}
      {!shellCtx && (
        <div className="flex items-center gap-2 text-sm mb-6 text-white/50">
          <button
            onClick={() => navigate(basePath)}
            className="hover:text-white transition"
          >
            Quiz
          </button>
          <span>›</span>
          <span className="text-white font-medium">{deck.titre}</span>
        </div>
      )}

      {!shellCtx ? (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-white">{deck.titre}</h1>
            <p className="text-sm text-white/60">
              {deck.keywords?.join(', ') || '—'} · {deck.education_level} · {deck.difficulty}
            </p>
          </div>
          <button
            onClick={() => navigate(`${basePath}/${deckId}/questions`)}
            className="btn-accent px-4 py-2"
          >
            📝 Gérer les questions
          </button>
        </div>
      ) : (
        <div className="mb-6 flex justify-end">
          <button
            onClick={() => navigate(`${basePath}/${deckId}/questions`)}
            className="btn-accent px-4 py-2"
          >
            📝 Gérer les questions
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-4 py-2 mb-4">
          {error}
        </div>
      )}

      {/* Sous-thèmes */}
      <div className={`border border-white/10 rounded-2xl p-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-900/80 backdrop-blur-sm'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Sous-thèmes</h2>
          <button onClick={() => setModal('create')} className="btn-primary px-3 py-1.5 text-sm">
            + Ajouter
          </button>
        </div>

        {deck.sous_themes?.length === 0 ? (
          <p className="text-sm py-4 text-center text-white/40">Aucun sous-thème.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-white/20">
              <tr>
                <th className="text-left py-2 font-medium text-white/60">Nom</th>
                <th className="text-center py-2 font-medium w-20 text-white/60">Ordre</th>
                <th className="py-2 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {deck.sous_themes.map(st => (
                <tr key={st.id} className="hover:bg-white/5">
                  <td className="py-2 font-medium text-white">{st.nom}</td>
                  <td className="py-2 text-center text-white/60">{st.ordre}</td>
                  <td className="py-2 flex gap-1 justify-end">
                    <button
                      onClick={() => setModal(st)}
                      className="px-3 text-xs py-1.5 rounded-lg transition text-white bg-white/10 hover:bg-white/20"
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(st)}
                      className="px-3 text-xs py-1.5 rounded-lg transition text-white bg-red-500/40 hover:bg-red-500/60"
                      title="Supprimer"
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={modal === 'create' ? 'Nouveau sous-thème' : 'Modifier le sous-thème'}
      >
        {modal !== null && (
          <SousThemeForm
            initial={modal !== 'create' ? modal : undefined}
            onSubmit={handleSubmit}
            onCancel={() => setModal(null)}
            loading={saving}
          />
        )}
      </Modal>
    </div>
  )
}
